import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SlidersHorizontal, X, Lock, Plus, Search } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import JobCard, { Listing } from '@/components/JobCard';
import Seo from '@/components/Seo';
import ErrorBoundary from '@/components/ErrorBoundary';
import BrowseState, { FeedState } from '@/components/BrowseState';
import {
  fetchListingsResult,
  fetchPurchasedIds,
  MAX_PAGE_SIZE,
  deriveFeedState,
  matchesCity,
  matchesMinBudget,
  matchesKeyword,
} from '@/lib/marketplace';

import { useOverlayLock } from '@/lib/overlay';
import { TRADES, US_STATES, tradeLabel } from '@/data/constants';
import PostPurchaseBanner from '@/components/PostPurchaseBanner';
import OnboardingTour from '@/components/OnboardingTour';
import { WelcomeTicket, readWelcomeTicket, clearWelcomeTicket, tourSeen } from '@/lib/activation';



const SCROLL_KEY = 'mkt_jobs_scroll';

const Jobs: React.FC = () => {
  const { t, path, lang } = useI18n();

  const { profile } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [jobs, setJobs] = useState<Listing[]>([]);
  const [purchased, setPurchased] = useState<string[]>([]);
  const [feed, setFeed] = useState<FeedState>('loading');
  const [retryAfter, setRetryAfter] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const restored = useRef(false);
  /** Monotonic request id: only the newest load may write state. */
  const requestId = useRef(0);

  // ---------------------------------------------------------------------
  // POST-PAYMENT WELCOME. The return page writes a ticket after the verified
  // webhook activated the credits, so this page can confirm the purchase and
  // open the first-buyer tour exactly once.
  // ---------------------------------------------------------------------
  const [ticket, setTicket] = useState<WelcomeTicket | null>(null);
  const [tour, setTour] = useState(false);

  useEffect(() => {
    const found = readWelcomeTicket();
    if (!found) return;
    setTicket(found);
    if (!tourSeen()) {
      // Small delay so the page paints behind the walkthrough first.
      const id = window.setTimeout(() => setTour(true), 600);
      return () => window.clearTimeout(id);
    }
  }, []);


  // The mobile filter sheet is a full-screen overlay: register it so the
  // floating Start Now / WhatsApp stack steps aside while it is open.
  useOverlayLock(showFilters);


  const trade = params.get('trade') || '';
  const state = params.get('state') || '';
  const type = params.get('type') || '';
  const minBudget = params.get('budget') || '';
  // City is PUBLIC on every card, so it is a real, working search filter.
  // It is applied to rows already in memory and NEVER to the initial request.
  const city = params.get('city') || '';
  /**
   * KEYWORD SEARCH (`?q=`).
   *
   * React Router has already percent-DECODED the value here, so `?q=roof%20repair`
   * and `?q=roof+repair` both arrive as "roof repair", and `?q=techado` keeps its
   * accents. Re-encoding happens automatically when URLSearchParams is
   * serialised back into the URL below, so the link stays copy-pasteable and
   * survives a refresh.
   *
   * It matches ONLY the public fields listed in KEYWORD_SEARCH_FIELDS and runs
   * in memory on rows the browse edge function has already stripped, so it can
   * never reach a homeowner name, phone, email, ZIP, street address, note or
   * purchased-lead record.
   */
  const q = params.get('q') || '';

  const hasFilters = !!(trade || state || type || minBudget || city.trim() || q.trim());



  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  // -------------------------------------------------------------------------
  // CITY + KEYWORD SEARCH BOXES — debounced, decoupled from the fetch.
  // Each input is local state so every keystroke re-renders only that field;
  // the URL (and therefore the filtering) updates 300ms after typing stops.
  // Because neither `city` nor `q` is a dependency of `load`, typing can never
  // cancel, restart or block the listings request.
  // -------------------------------------------------------------------------
  const [cityInput, setCityInput] = useState(city);
  const [qInput, setQInput] = useState(q);

  // Keep the boxes in sync when the URL changes from outside (Clear, back
  // button, or a shared /en/jobs?q=roofing link opened directly).
  useEffect(() => {
    setCityInput(city);
  }, [city]);

  useEffect(() => {
    setQInput(q);
  }, [q]);

  useEffect(() => {
    if (cityInput.trim() === city.trim()) return;
    const id = window.setTimeout(() => {
      const next = new URLSearchParams(params);
      const value = cityInput.trim();
      // An empty box must never add a filter condition.
      if (value) next.set('city', value);
      else next.delete('city');
      setParams(next, { replace: true });
    }, 300);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityInput]);

  useEffect(() => {
    if (qInput.trim() === q.trim()) return;
    const id = window.setTimeout(() => {
      const next = new URLSearchParams(params);
      const value = qInput.trim();
      // An empty search box removes `q` entirely rather than leaving ?q= behind.
      if (value) next.set('q', value);
      else next.delete('q');
      setParams(next, { replace: true });
    }, 300);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);


  /**
   * Load page 0.
   *
   * GUARANTEE: this ALWAYS leaves the loading state. `fetchListingsResult`
   * cannot reject or hang (it has its own deadline), and the try/catch/finally
   * below is the second belt — an unexpected throw becomes a visible error card
   * with a Retry button instead of an endless "Loading projects…" spinner,
   * which is exactly the failure this page regressed into.
   *
   * The `requestId` guard means a slow in-flight response from an older filter
   * combination can never overwrite a newer one.
   */
  const load = useCallback(async () => {
    const myId = ++requestId.current;
    setFeed('loading');
    try {
      const res = await fetchListingsResult({ trade, state, type, limit: MAX_PAGE_SIZE, offset: 0 });
      if (myId !== requestId.current) return; // superseded by a newer request

      if (res.status !== 'ok') {
        console.error('[Jobs] Failed to load public listings:', res.status, res.detail || '');
        setJobs([]);
        setOffset(0);
        setHasMore(false);
        setRetryAfter(res.retryAfter);
        setFeed(res.status);
        return;
      }
      setJobs(res.listings);
      setOffset(res.listings.length);
      setHasMore(res.listings.length >= MAX_PAGE_SIZE);
      setFeed(res.listings.length === 0 ? 'empty' : 'ok');
    } catch (error) {
      // Unreachable in normal operation — kept so a future refactor of the
      // fetch layer can never resurrect the infinite spinner.
      console.error('[Jobs] Failed to load public listings:', error);
      if (myId !== requestId.current) return;
      setJobs([]);
      setOffset(0);
      setHasMore(false);
      setRetryAfter(0);
      setFeed('server_error');
    } finally {
      // Belt and braces: if any branch above somehow skipped a terminal state,
      // the page still leaves "loading".
      if (myId === requestId.current) {
        setFeed((prev) => (prev === 'loading' ? 'server_error' : prev));
      }
    }
  }, [trade, state, type]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await fetchListingsResult({ trade, state, type, limit: MAX_PAGE_SIZE, offset });
      if (res.status === 'ok') {
        setJobs((prev) => {
          const seen = new Set(prev.map((p) => p.id));
          return [...prev, ...res.listings.filter((l) => !seen.has(l.id))];
        });
        setOffset((o) => o + res.listings.length);
        setHasMore(res.listings.length >= MAX_PAGE_SIZE);
      } else {
        console.error('[Jobs] Failed to load more listings:', res.status, res.detail || '');
        setRetryAfter(res.retryAfter);
        setFeed(res.status);
      }
    } catch (error) {
      console.error('[Jobs] Failed to load more listings:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchPurchasedIds(profile?.id).then(setPurchased).catch(() => setPurchased([]));
  }, [profile?.id]);

  // Returning from a job detail page keeps the results and the scroll position.
  useEffect(() => {
    if (feed !== 'ok' || restored.current) return;
    const saved = Number(sessionStorage.getItem(SCROLL_KEY) || 0);
    if (saved > 0) window.scrollTo(0, saved);
    restored.current = true;
  }, [feed]);

  /**
   * Client-side refinement ONLY.
   * City IS public now ("Fontana, CA"), so a city search is safe and useful —
   * it matches the same value the cards render. ZIP, street address and the
   * homeowner's name are never in this payload at all.
   *
   * Every matcher here is shared with the regression suite and returns TRUE for
   * an empty needle, so a blank box can never hide listings, and a listing with
   * a null field is only excluded when the user actually typed something.
   *
   * The keyword term is ANDed with trade / state / type (server side) and with
   * city / budget (in memory), so `?q=` refines the filtered set rather than
   * replacing it.
   */
  const filtered = useMemo(() => {
    return jobs.filter((j) => {
      if (!matchesCity(j.city, city)) return false;
      // Budget uses the REAL columns (budget_max / budget_min) and falls back to
      // the largest number in the free-text range. Listings with no budget on
      // file are never hidden.
      if (!matchesMinBudget(j, minBudget)) return false;
      // KEYWORD: public fields only, plus the localized trade label so a
      // Spanish visitor searching "techado" matches the `roofing` slug.
      if (!matchesKeyword(j as unknown as Record<string, unknown>, q, [tradeLabel(j.trade, lang)])) {
        return false;
      }
      return true;
    });
  }, [jobs, city, minBudget, q, lang]);





  const clear = () => setParams(new URLSearchParams(), { replace: true });

  const onUnlock = (job: Listing) => {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
    navigate(path(`/jobs/${job.slug}`));
  };

  const selectCls =
    'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 focus:border-slate-900 focus:outline-none';
  const labelCls = 'mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500';

  const filterPanel = (
    <div className="grid gap-4">
      <div>
        <label className={labelCls} htmlFor="jobs-trade">{t('jobs.filterTrade')}</label>
        <select id="jobs-trade" className={selectCls} value={trade} onChange={(e) => setParam('trade', e.target.value)}>
          <option value="">{t('common.all')}</option>
          {TRADES.map((tr) => (
            <option key={tr.slug} value={tr.slug}>{tradeLabel(tr.slug, lang)}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls} htmlFor="jobs-state">{t('jobs.filterState')}</label>
        <select id="jobs-state" className={selectCls} value={state} onChange={(e) => setParam('state', e.target.value)}>
          <option value="">{t('common.all')}</option>
          {US_STATES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/*
        CITY SEARCH — city is public on every card ("Fontana, CA"), so this box
        matches exactly what the contractor sees. The street address, ZIP and
        homeowner contact are still only released after purchase.
      */}
      <div>
        <label className={labelCls} htmlFor="jobs-city">
          {t('jobs.filterCity')}
        </label>
        <input
          id="jobs-city"
          type="text"
          value={cityInput}
          onChange={(e) => setCityInput(e.target.value)}
          placeholder={t('jobs.filterCity')}
          className={selectCls}
          autoComplete="address-level2"
        />
      </div>

      <p className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs font-medium text-slate-500">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {t('jobs.exactLocationHidden')}
      </p>

      {/*
        RADIUS FILTER REMOVED (production audit).
        There are no coordinates for a lead before purchase — ZIP is masked and
        there is no lat/lng column — so the old "within N miles" control was
        filtering on a number hashed from the listing id. It hid real leads at
        random. Use State + City until real geodata exists.
      */}
      <div>
        <label className={labelCls} htmlFor="jobs-budget">{t('jobs.filterBudget')}</label>
        <select
          id="jobs-budget"
          className={selectCls}
          value={minBudget}
          onChange={(e) => setParam('budget', e.target.value)}
        >
          <option value="">{t('common.all')}</option>
          {[5000, 15000, 30000, 60000].map((b) => (
            <option key={b} value={b}>${b.toLocaleString()}+</option>
          ))}
        </select>
      </div>

      <div>
        <label className={labelCls}>{t('jobs.filterType')}</label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { v: '', l: t('common.all') },
            { v: 'residential', l: t('common.residential') },
            { v: 'commercial', l: t('common.commercial') },
          ].map((o) => (
            <button
              key={o.v || 'all'}
              onClick={() => setParam('type', o.v)}
              className={`rounded-lg border px-2 py-2 text-xs font-bold transition-colors ${
                type === o.v ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-600'
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>
      <button onClick={clear} className="rounded-lg border border-slate-300 px-3 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50">
        {t('common.clear')}
      </button>
    </div>
  );

  // 'empty' ONLY when the request genuinely succeeded with zero rows.
  // deriveFeedState is shared with the permanent regression suite, so the logic
  // under test is literally the logic that renders.
  const shown: FeedState =
    feed === 'loading' || feed === 'empty'
      ? feed
      : deriveFeedState(feed, filtered.length);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Seo
        title={trade ? `${tradeLabel(trade, lang)} ${lang === 'es' ? 'trabajos' : 'jobs'}` : t('jobs.title')}
        description={t('jobs.subtitle')}
      />
      {/*
        POST-PURCHASE LANDING. This is the primary automatic destination after a
        successful payment, because this is where credits get spent. The banner
        confirms the purchase and the balance; the tour explains the next step.
      */}
      {ticket && (
        <PostPurchaseBanner
          ticket={ticket}
          onProjects
          onDismiss={() => {
            clearWelcomeTicket();
            setTicket(null);
          }}
          onReplayTour={() => setTour(true)}
        />
      )}

      <OnboardingTour
        open={tour}
        onClose={() => setTour(false)}
        plan={ticket?.plan}
        credits={ticket?.credits}
        jobsHref={path('/jobs')}
      />

      <div className="flex flex-wrap items-end justify-between gap-4">

        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{t('jobs.title')}</h1>
          <p className="mt-2 max-w-2xl text-slate-500">{t('jobs.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowFilters(true)}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 lg:hidden"
        >
          <SlidersHorizontal className="h-4 w-4" /> {t('common.filters')}
        </button>
      </div>

      {/*
        PUBLIC KEYWORD SEARCH.
        A real <form> so Enter submits on every browser and mobile keyboard
        (which also flushes the 300ms debounce immediately). The URL is the
        single source of truth: typing writes `?q=`, and a shared or refreshed
        /en/jobs?q=roofing link restores the exact same result set.
      */}
      <form
        role="search"
        className="mt-6"
        onSubmit={(e) => {
          e.preventDefault();
          const next = new URLSearchParams(params);
          const value = qInput.trim();
          if (value) next.set('q', value);
          else next.delete('q');
          setParams(next, { replace: true });
        }}
      >
        <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500" htmlFor="jobs-q">
          {t('jobs.searchLabel')}
        </label>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
          <input
            id="jobs-q"
            type="search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder={t('jobs.searchPlaceholder')}
            aria-describedby="jobs-q-hint"
            autoComplete="off"
            className="w-full rounded-lg border border-slate-300 bg-white py-3 pl-10 pr-24 text-sm text-slate-800 focus:border-slate-900 focus:outline-none"
          />
          {q.trim() && (
            <button
              type="button"
              onClick={() => setQInput('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100"
            >
              {t('jobs.searchClear')}
            </button>
          )}
        </div>
        <p id="jobs-q-hint" className="mt-1.5 text-xs font-medium text-slate-400">
          {t('jobs.searchHint')}
        </p>
      </form>

      <div className="mt-8 grid gap-8 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-2xl border border-slate-200 bg-white p-5">
            <h2 className="mb-4 text-sm font-extrabold uppercase tracking-wide text-slate-900">{t('common.filters')}</h2>
            {filterPanel}
          </div>
        </aside>

        <div>
          {/* Announced to screen readers whenever the result count changes. */}
          <p className="mb-4 text-sm font-semibold text-slate-500" role="status" aria-live="polite">
            {feed === 'loading' ? t('jobs.loading') : `${filtered.length} ${t('jobs.results')}`}
          </p>


          {shown !== 'ok' ? (
            <BrowseState
              state={shown}
              retryAfter={retryAfter}
              onRetry={load}
              onClearFilters={clear}
              hasFilters={hasFilters}
              query={q}
            />

          ) : (
            /*
              A crash inside ONE card must never blank the whole marketplace (and
              must never look like "still loading"). The boundary reports to
              Sentry/observability and renders its own recovery card.
            */
            <ErrorBoundary area="jobs-grid">
              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {filtered.map((j) => (
                  <JobCard key={j.id} job={j} unlocked={purchased.includes(j.id)} onUnlock={onUnlock} />
                ))}
              </div>
              {hasMore && (
                <div className="mt-8 flex justify-center">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-3 text-sm font-bold text-slate-800 hover:bg-slate-50 disabled:opacity-60"
                  >
                    <Plus className="h-4 w-4" />
                    {loadingMore ? t('common.loading') : t('common.viewAll')}
                  </button>
                </div>
              )}
            </ErrorBoundary>
          )}

        </div>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-[90] bg-slate-900/50 lg:hidden" onClick={() => setShowFilters(false)}>
          <div
            className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-extrabold text-slate-900">{t('common.filters')}</h2>
              <button onClick={() => setShowFilters(false)} className="rounded-lg p-2 text-slate-400">
                <X className="h-5 w-5" />
              </button>
            </div>
            {filterPanel}
            <button
              onClick={() => setShowFilters(false)}
              className="mt-4 w-full rounded-lg bg-slate-900 px-4 py-3 text-sm font-bold text-white"
            >
              {t('common.continue')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;
